import { useState } from 'react';
import { useRequestsContext } from '@/common/hooks/requestHook';
import {
	analyzeCallByCallingInformationId,
	manualReviewCallAnalysis,
} from '@/API/repositories/callAnalysis';
import { getTranscriptionById } from '@/API/repositories/transcription';
import { REVIEW_MODE } from '../helpers/labels';

const getStoredDocumentId = (analysis) =>
	analysis?.stored_document?._id ||
	analysis?.stored_document ||
	analysis?.calling_information?.stored_data?._id ||
	analysis?.calling_information?.stored_data ||
	null;

const pickAnalysisTranscripts = (transcriptions) => {
	if (!Array.isArray(transcriptions) || !transcriptions.length) {
		return [];
	}

	const en = transcriptions.find((item) => item.lang === 'en');
	if (en) {
		return [en];
	}

	return transcriptions;
};

const useCallAnalysisDetail = ({ analysis, onUpdated }) => {
	const { makeRequest } = useRequestsContext();
	const [reviewMode, setReviewMode] = useState(null);
	const [comment, setComment] = useState('');
	const [correctedScore, setCorrectedScore] = useState(
		analysis?.overall_score ?? 0,
	);
	const [feedback, setFeedback] = useState('');
	const [localAnalysis, setLocalAnalysis] = useState(analysis);
	const [showTranscript, setShowTranscript] = useState(false);
	const [transcripts, setTranscripts] = useState(null);
	const [transcriptError, setTranscriptError] = useState(null);
	const [transcriptLoading, setTranscriptLoading] = useState(false);

	const storedDocumentId = getStoredDocumentId(localAnalysis);

	const loadTranscript = async () => {
		if (!storedDocumentId || transcripts || transcriptLoading) {
			return;
		}

		setTranscriptLoading(true);
		setTranscriptError(null);

		const response = await makeRequest(
			getTranscriptionById.bind(null, storedDocumentId),
		);

		setTranscriptLoading(false);

		if (!response?.data || response.data === false) {
			setTranscriptError('No transcription available for this call.');
			setTranscripts([]);
			return;
		}

		setTranscripts(pickAnalysisTranscripts(response.data));
	};

	const handleToggleTranscript = async () => {
		const next = !showTranscript;
		setShowTranscript(next);

		if (next) {
			await loadTranscript();
		}
	};

	const handleReview = async (status) => {
		const payload = {
			status,
			comment: comment || undefined,
			feedback_for_system: feedback || undefined,
		};

		if (status === REVIEW_MODE.CORRECTED) {
			payload.corrected_score = Number(correctedScore);
		}

		const response = await makeRequest(
			manualReviewCallAnalysis.bind(null, localAnalysis._id, payload),
		);

		if (response?.data) {
			setLocalAnalysis(response.data);
			setReviewMode(null);
			onUpdated?.(response.data);
		}
	};

	const handleReanalyze = async () => {
		const callingInformationId =
			localAnalysis.calling_information?._id ||
			localAnalysis.calling_information;

		if (!callingInformationId) {
			return;
		}

		const response = await makeRequest(
			analyzeCallByCallingInformationId.bind(
				null,
				callingInformationId,
				true,
			),
		);

		if (response?.data && !response.data.error) {
			setLocalAnalysis(response.data);
			setTranscripts(null);
			setShowTranscript(false);
			onUpdated?.(response.data);
		}
	};

	return {
		localAnalysis,
		storedDocumentId,
		reviewMode,
		setReviewMode,
		comment,
		setComment,
		correctedScore,
		setCorrectedScore,
		feedback,
		setFeedback,
		showTranscript,
		transcripts,
		transcriptError,
		transcriptLoading,
		handleToggleTranscript,
		handleReview,
		handleReanalyze,
	};
};

export default useCallAnalysisDetail;
